import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import ProblemIdLink from '../ProblemHubV2/ProblemIdLink';
import { NEED_META } from '../ProblemHubV2/problemApi';
import PaperTable from '../ExamPapersV2/PaperTable';
import { fetchPaper, listFolders } from '../ExamPapersV2/examPaperApi';
import { fetchAllProjectProblems } from './ocrApi';
import { FOLD_MAX, promotedBadgeRows, paperLoadOutcome } from './resultLinkRules';

/**
 * 「이 프로젝트가 만든 것」으로 가는 길 — ⑥ 등록된 문제 번호 · ⑦ 만든 시험지 한 줄.
 * 설계: docs/ocr-hub-v2/0925_SPEC_ocr-result-links.html
 *
 * 둘 다 **펼친 단계 안에서만 산다** — StepRow 는 펼친 단계의 본문만 그리므로 접혀 있으면 조회가 0 이다.
 * 5초 폴링과 묶지 않는다: 폴링은 상세 객체를 매번 새로 갈아 끼우는데, 여기는 숫자 하나(⑥ 등록 건수)
 * 또는 번호 하나(⑦ 시험지 번호)에만 반응한다.
 */

// ⑦ 조회 실패 때 남기는 시험지 링크의 초록 알약(검수 창 「등록됨 · 61590」 알약과 같은 값).
// ⑥ 문제 번호에는 알약을 쓰지 않는다 — 다른 화면의 문제 번호와 같은 얼굴이어야 한다(오너 지시 2026-09-25).
const PAPER_PILL = 'inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800';
/**
 * ⑥ 번호의 색 — 문제 v2 목록과 같은 기준(완비 = 메인 녹색)으로 세우기 위한 완비 표시.
 * OCR 로 등록되는 문제는 **AI 분석까지 끝나고 그 결과가 승인된 것뿐**이다(⑥ 등록 조건, 오너 결정 2026-08-30 —
 * promoteStage.splitByStage). AI 분석이 개념·난이도·정답·풀이를 채우므로 네 칸이 모두 찬 문제로 본다
 * (오너 확인 2026-09-25 「AI 분석된 것들은 모두 메인 녹색」). 정본 부품을 고치지 않고 need_* 꼴로 넘긴다.
 * ⚠ 8월 30일 이전에는 분석 없이도 등록됐다 — 그 무렵 프로젝트의 번호도 녹색으로 선다.
 */
const AI_ANALYZED = Object.fromEntries(NEED_META.map((m) => [m.key, false]));
// 링크가 아닌 회색 알약 — 지워진 시험지
const GONE_PILL = 'inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500';

/**
 * ⑥ 등록된 문제 번호.
 *
 * 번호는 문제 번호 정본 부품(ProblemIdLink)의 **기본 얼굴 그대로**다 — 고정폭·굵게·12px·맨숫자, 누르면 문제 상세.
 * 문제 v2 목록 표·배포 채점 표·오답노트와 같은 부품이다. 색은 문제 v2 목록과 같은 완비 기준이고,
 * OCR 등록분은 AI 분석을 거친 것뿐이라 메인 녹색으로 선다(위 AI_ANALYZED).
 *
 * promotedCount: 상세의 promoted_count(진행 집계). 0 이면 조회도 표시도 하지 않는다 — 머리 요약
 * 「등록 0/22건」이 이미 같은 말을 한다. null(집계를 안 주는 옛 서버)이면 펼칠 때 한 번만 읽는다.
 *
 * 다시 읽는 조건은 등록 건수 하나다. 목록 새로고침 키(problemsReloadKey)에 매지 않는다 — 그 키는
 * 승인 한 건마다 올라가서, 매면 검수하는 동안 목록 전량을 계속 다시 읽는다.
 * [다시 등록](갱신)은 같은 은행 문제를 덮어써 번호가 그대로이므로 건수가 같으면 목록도 같다.
 */
export function PromotedProblemLinks({ projectId, promotedCount = null, getAccessToken }) {
  const [rows, setRows] = useState(null); // null = 아직 모름
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState(null);
  // 펼침의 첫 값은 처음 받은 목록으로 한 번만 정한다. 다시 읽어도 사람이 접거나 편 상태를 덮지 않는다
  const [open, setOpen] = useState(null);
  const skip = promotedCount === 0;

  useEffect(() => {
    if (skip) return undefined;
    let alive = true;
    fetchAllProjectProblems(projectId, getAccessToken)
      .then((d) => {
        if (!alive) return;
        const next = promotedBadgeRows(d.items);
        setRows(next);
        setTruncated(!!d.truncated);
        setError(null);
        setOpen((prev) => (prev == null ? next.length <= FOLD_MAX : prev));
      })
      .catch((e) => {
        // 다시 읽기가 실패해도 직전 번호 목록을 걷지 않는다(분석 지도와 같은 방식) — 실패 사실은 머리 아래 한 줄이 말한다
        if (alive) setError(e?.message || '등록된 문제를 불러오지 못했습니다.');
      });
    return () => { alive = false; };
  }, [projectId, promotedCount, skip, getAccessToken]);

  if (skip) return null;
  // 「읽는 중」은 처음 한 번만 자리를 차지한다 — 다시 읽을 때 번호가 사라졌다 돌아오면 깜빡인다
  if (rows == null) {
    return error
      ? <p className="mt-3 text-xs text-danger">{error}</p>
      : <p className="mt-3 text-xs text-gray-400">등록된 문제를 읽는 중…</p>;
  }
  if (rows.length === 0) return error ? <p className="mt-3 text-xs text-danger">{error}</p> : null;

  const isOpen = open ?? rows.length <= FOLD_MAX;
  return (
    <div className="mt-3">
      {/* 머리는 ② 분리+OCR 의 「파일 목록 (N)」 접기 머리와 같은 얼굴이다 */}
      <button
        type="button"
        onClick={() => setOpen(!isOpen)}
        aria-expanded={isOpen}
        className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
      >
        {isOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        등록된 문제 ({rows.length})
      </button>
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
      {isOpen && (
        // 번호끼리의 간격 6px 은 알약이던 때의 값을 그대로 둔다(가로로 번호를 늘어놓는 형제 화면이 없다).
        // 기준선 정렬 — 고정폭 숫자와 「삭제됨」(본문 글꼴)이 한 줄에 선다
        <div className="mt-2 flex flex-wrap items-baseline gap-1.5">
          {rows.map((r) => {
            const where = r.index != null ? `인덱스 ${r.index} — ` : '';
            return r.pid != null ? (
              <ProblemIdLink
                key={r.key}
                id={r.pid}
                item={AI_ANALYZED}
                title={`${where}선택하면 문제 메뉴에서 엽니다 (⌘·Ctrl 클릭은 새 탭)`}
              />
            ) : (
              // 지워진 문제 — 번호 자리를 비우지 않고 이유를 글자로 남긴다. 얼굴은 이 단계 안내 줄과 같은 12px 회색
              <span key={r.key} className="text-xs text-gray-400" title={`${where}문제은행에서 지워져 열 수 없습니다`}>
                삭제됨
              </span>
            );
          })}
        </div>
      )}
      {truncated && (
        <p className="mt-1 text-xs text-amber-700">목록을 일부만 읽었습니다 — 보이는 번호가 실제보다 적을 수 있습니다.</p>
      )}
    </div>
  );
}

/**
 * ⑦ 이 프로젝트를 종료하며 만든 시험지 — 시험지 v2 표 한 줄.
 *
 * 표는 시험지 메뉴의 부품(PaperTable)을 그대로 쓴다. 문제 상세 [시험지] 탭(PaperUsageTab)이 같은 방식이다 —
 * 같은 시험지가 두 메뉴에서 다른 얼굴이면 같은 것인 줄 모른다. 이동·복사·삭제 콜백을 넘기지 않으므로
 * 「작업」 칸은 서지 않고 #ID · 시험지 · 문항수 · 폴더 · 작성자 · 수정일 여섯 칸이 된다.
 *
 * 조회 결과는 다섯 갈래다(설계 §6).
 *   번호 없음 — 「연결된 시험지가 없습니다」. 시험지 없이 종료했거나, 서버가 시험지 삭제 뒤 번호를 비우는 경우다.
 *               삭제 뒤 번호가 비워지는지는 계약에 없어서 두 경우에 다 맞는 문장만 쓴다.
 *   200       — 표 한 줄. 행을 누르면 시험지 화면
 *   404       — 지워진 시험지. 회색 알약, 링크 없음(지워진 번호로 가면 시험지 화면에 「찾을 수 없습니다」만 뜬다)
 *   그 밖     — 지워졌는지 모른다. 빨간 한 줄 + [다시 시도] + 초록 링크를 남긴다
 *   401       — 공용 처리가 로그인 화면으로 보낸다. 조회 함수는 그 직전에 상태 코드 없는 실패로 끝나
 *               잠깐 빨간 줄이 비칠 수 있으나 곧 화면이 바뀌므로 따로 가르지 않는다.
 *
 * 조회는 펼칠 때(마운트)와 [다시 시도]뿐이다. 펼쳐 둔 사이에 지워지면 옛 줄이 남는다 — 접고 다시 펴면
 * 새로 읽는다. 드문 일에 5초마다 시험지를 읽지 않기 위한 선택이다.
 */
export function ResultPaperRow({ paperId = null, closed = false, getAccessToken }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: 'loading' }); // loading | ok | gone | error
  // 닫힌 뒤(접힘)·다시 부른 뒤에 도착한 옛 응답이 화면을 덮지 않게 한다
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = (seqRef.current += 1);
    setState({ phase: 'loading' });
    try {
      const [paper, folders] = await Promise.all([
        fetchPaper(paperId, getAccessToken),
        // 폴더 칸의 「부모 › 자식」 경로 재료. 실패는 삼킨다 — 표는 서야 하고, 그때는 서버가 준
        // folder_name 하나로 떨어진다(문제 상세 [시험지] 탭의 loadFoldersQuietly 와 같은 규칙).
        listFolders({}, getAccessToken)
          .then((f) => (Array.isArray(f?.items) ? f.items : []))
          .catch(() => []),
      ]);
      if (seq !== seqRef.current) return;
      setState({ phase: 'ok', paper, folders });
    } catch (e) {
      if (seq !== seqRef.current) return;
      setState({ phase: paperLoadOutcome(e), message: e?.message || '' });
    }
  }, [paperId, getAccessToken]);

  useEffect(() => {
    if (!closed || paperId == null) return undefined;
    load();
    return () => { seqRef.current += 1; };
  }, [closed, paperId, load]);

  if (!closed) return null; // 시험지는 종료할 때 생긴다
  if (paperId == null) return <p className="mt-3 text-xs text-gray-400">연결된 시험지가 없습니다.</p>;

  if (state.phase === 'loading') return <p className="mt-3 text-xs text-gray-400">시험지 정보를 읽는 중…</p>;

  if (state.phase === 'ok') {
    return (
      <div className="mt-3">
        <PaperTable
          items={[state.paper]}
          folders={state.folders}
          onSelect={(id) => { if (id != null) navigate(`/exam-papers/paper/${id}`); }}
        />
      </div>
    );
  }

  if (state.phase === 'gone') {
    return (
      <div className="mt-3 space-y-1">
        <span className={GONE_PILL} title={`시험지 #${paperId}은 지워져 열 수 없습니다`}>
          시험지 <span className="font-mono font-bold">#{paperId}</span> · 삭제됨
        </span>
        <p className="text-xs text-gray-500">
          이 프로젝트로 만든 시험지가 지워졌습니다. 종료된 프로젝트는 시험지를 다시 만들 수 없습니다 —
          필요하면 [시험지]에서 새로 만드세요. 등록된 문제는 ⑥에 그대로 있습니다.
        </p>
      </div>
    );
  }

  // 그 밖의 실패 — 지워졌는지 모르므로 가는 길을 닫지 않는다. 눌러서 시험지 화면이 진짜 사유를 보이게 둔다
  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs text-danger">
        시험지 정보를 불러오지 못했습니다{state.message ? ` — ${state.message}` : '.'}
        {' '}
        <button type="button" onClick={load} className="underline hover:text-red-700">다시 시도</button>
      </p>
      {/* 진짜 링크 — ⌘·Ctrl 클릭의 새 탭은 react-router 의 Link 가 브라우저에 맡긴다 */}
      <Link
        to={`/exam-papers/paper/${paperId}`}
        aria-label={`시험지 ${paperId}번 열기`}
        title="시험지 정보를 불러오지 못했지만 시험지 화면에서 직접 열어 볼 수 있습니다 (⌘·Ctrl 클릭은 새 탭)"
        className={`${PAPER_PILL} gap-1 font-medium underline-offset-2 hover:underline`}
      >
        시험지 <span className="font-mono font-bold">#{paperId}</span>
      </Link>
    </div>
  );
}
